import toastConfig from '@/config/toast/toast-config'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { type ReactNode, useEffect, useState } from 'react'
import {
	KeyboardAvoidingView,
	Modal,
	Platform,
	Pressable as RNPressable,
	ScrollView,
	StyleSheet,
	View,
	useWindowDimensions,
} from 'react-native'
import Animated, {
	Easing,
	ReduceMotion,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Toast from 'react-native-toast-message'

const OUT_MS = 220
const IN_MS = 320
const CURVE = Easing.bezier(0.2, 0, 0, 1)

/** The most of the screen the card may take, and the chrome above the scroll inside it. */
const SHOWN = 0.88
const GRABBER = 24

/**
 * A white sheet that slides up over the page for a short, self-contained task: a list to pick
 * from, a kit to claim. The page dims in place while only the card moves, and the dim covers
 * everything, including the space beyond the card's rounded corners. Tapping the dimmed page
 * closes it. Soft-cornered at 24, like every card on the page; the grabber is the only ornament.
 */
export { SheetTitle } from './SheetTitle'

export const Sheet = ({
	open,
	onClose,
	children,
}: { open: boolean; onClose: () => void; children: ReactNode }) => {
	const insets = useSafeAreaInsets()
	const { height: screenHeight } = useWindowDimensions()
	// What the content actually comes to, measured on a plain view around it.
	//
	// The scroll view cannot be asked. A scroll view does not stand as tall as what it holds, so
	// left to size itself it keeps whatever it happened to measure first: a sheet that fetches on
	// the way in was the height of the skeleton it opened on, and the record that replaced it grew
	// inside a scroll view that never grew with it, giving a card two and a half rows tall with
	// nothing to scroll. Measured here, the scroll is told its height: exactly the content while
	// the content fits, and the cap once it does not, which is the point where the rest becomes
	// scroll.
	const [contentHeight, setContentHeight] = useState(0)
	const pad = Math.max(insets.bottom, 12)
	const room = screenHeight * SHOWN - GRABBER
	// The modal stays mounted for a beat after `open` drops so the card can slide out.
	const [mounted, setMounted] = useState(open)
	useEffect(() => {
		if (open) {
			setMounted(true)
			return
		}
		const timer = setTimeout(() => setMounted(false), OUT_MS)
		return () => clearTimeout(timer)
	}, [open])

	// How far down the card is sitting, one screen away to none, and the dim that goes with it.
	//
	// The card is moved by a value of its own rather than by an entering animation, because those
	// take their travel from the height the view had when they started. A sheet that fetches on the
	// way in grows while the slide is still running, and the slide ends wherever the arithmetic of
	// the old height left it: the card comes to rest pushed down off the screen's foot by however
	// much it grew, showing a couple of rows of a record that is sitting mostly below the phone.
	// Driven this way it always comes to rest at nought, whatever happens to its height on the way.
	const slide = useSharedValue(open ? 0 : 1)
	useEffect(() => {
		slide.value = withTiming(open ? 0 : 1, {
			duration: open ? IN_MS : OUT_MS,
			easing: CURVE,
			reduceMotion: ReduceMotion.System,
		})
	}, [open, slide])
	const cardStyle = useAnimatedStyle(() => ({
		transform: [{ translateY: slide.value * screenHeight }],
	}))
	const dimStyle = useAnimatedStyle(() => ({ opacity: 1 - slide.value }))

	return (
		<Modal
			visible={mounted}
			transparent
			animationType="none"
			onRequestClose={onClose}
			statusBarTranslucent
		>
			<Animated.View
				pointerEvents="none"
				style={[
					StyleSheet.absoluteFill,
					{ backgroundColor: 'rgba(0,0,0,0.45)' },
					dimStyle,
				]}
			/>
			<KeyboardAvoidingView
				className="flex-1 justify-end"
				behavior={Platform.OS === 'ios' ? 'padding' : undefined}
			>
				{/* The dimmed page closes the sheet on a tap; VoiceOver skips it and uses the escape gesture. */}
				<RNPressable
					accessibilityElementsHidden
					importantForAccessibility="no-hide-descendants"
					onPress={onClose}
					style={{ flex: 1 }}
				/>
				<Animated.View
					className="rounded-t-[24px] bg-white px-5 pt-2.5"
					style={[{ maxHeight: '88%' }, cardStyle]}
					accessibilityViewIsModal
					onAccessibilityEscape={onClose}
				>
					<Pressable
						onPress={onClose}
						haptic={false}
						hitSlop={12}
						accessibilityRole="button"
						accessibilityLabel="Close"
						className="items-center py-1"
					>
						<View
							className="h-1 w-9 rounded-full"
							style={{ backgroundColor: colors.line }}
						/>
					</Pressable>
					{/* Long content scrolls inside the sheet rather than pushing the top off the screen. The
						    scroll runs to the sheet's very bottom, so mid-scroll a row slips under the screen edge
						    the way any list does, and the padding lives in the content: at the end of the scroll
						    the last row clears the home indicator and the screen's corners, and no more. */}
					<ScrollView
						keyboardShouldPersistTaps="handled"
						showsVerticalScrollIndicator={false}
						bounces={false}
						style={
							contentHeight > 0
								? { height: Math.min(contentHeight + pad, room) }
								: undefined
						}
						contentContainerStyle={{ paddingBottom: pad }}
					>
						<View onLayout={(event) => setContentHeight(event.nativeEvent.layout.height)}>
							{children}
						</View>
					</ScrollView>
				</Animated.View>
			</KeyboardAvoidingView>
			{/* A second toast host, inside the modal.
			
			    A sheet is a React Native `Modal`, which on iOS is a separate view controller over
			    the app's window — so the one `<Toast />` mounted in the root layout is in the
			    window underneath. It still renders, which is the part that fooled us: the toast
			    comes up, and the sheet's own dim lies over the top of it, so a member gets a
			    greyed-out panel behind a scrim rather than a message.
			    
			    `react-native-toast-message` drives every mounted host off one store, so this one
			    shows the same toast at the same offset, a window higher. The root host still
			    paints its copy underneath; this one covers it exactly, and the copy that is
			    legible is the one in front. Mounted after the card so it also clears the sheet
			    itself, and configured from the same place, so a toast looks the same wherever it
			    is raised from. */}
			<Toast config={toastConfig} />
		</Modal>
	)
}
